"use client";
import type { StateCreator } from "zustand";
import { markSpotifyForceReauth } from "@/lib/spotifyAuth";
import { requestMediaPlaybackStop } from "@/lib/mediaPlayback";
import type { SessionSlice } from "./session.slice";

export type AuthUser = {
  user_id: string | null;
  spotify_id?: string | null;
  user_code?: string | null;
  is_admin?: boolean;
  provider?: string;
  created_at?: string | null;
  display_name?: string | null;
  email?: string | null;
  photo_url?: string | null;
};

export type AuthSlice = {
  user: AuthUser | null;
  authStatus: "checking" | "authed" | "unauth";
  error: string | null;

  checkSession: () => Promise<void>;
  logout: () => Promise<void>;
};

type AuthSliceState = AuthSlice & {
  sessionId?: string | null;
  sessionActive?: SessionSlice["sessionActive"];
  setBlockedSessionId?: SessionSlice["setBlockedSessionId"];
  resetSession?: SessionSlice["resetSession"];
};

function errorMessage(err: unknown, fallback: string) {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function parseAuthUser(payload: unknown): AuthUser | null {
  if (!payload || typeof payload !== "object") return null;

  const record = payload as Record<string, unknown>;
  const candidate =
    record.user && typeof record.user === "object"
      ? (record.user as Record<string, unknown>)
      : record;

  const userId =
    typeof candidate.user_id === "string" ? candidate.user_id.trim() : "";
  if (!userId) return null;

  return candidate as AuthUser;
}

async function hydrateSpotifyOwnerId(user: AuthUser): Promise<AuthUser> {
  if (user.provider !== "spotify" || user.spotify_id) {
    return user;
  }

  try {
    const res = await fetch("/api/spotify/me", {
      credentials: "include",
    });
    if (!res.ok) {
      return user;
    }

    const payload = await res.json().catch(() => null);
    const hydratedUser = parseAuthUser(payload);
    if (!hydratedUser) {
      return user;
    }

    return {
      ...user,
      spotify_id: hydratedUser.spotify_id ?? null,
      display_name: hydratedUser.display_name ?? user.display_name ?? null,
      email: hydratedUser.email ?? user.email ?? null,
      photo_url: hydratedUser.photo_url ?? user.photo_url ?? null,
    };
  } catch {
    return user;
  }
}

export const createAuthSlice: StateCreator<AuthSliceState, [], [], AuthSlice> = (
  set,
  get
) => ({
  user: null,
  authStatus: "checking",
  error: null,

  checkSession: async () => {
    try {
      set({ authStatus: "checking", error: null });

      // `/api/spotify/me` currently doubles as the provider-neutral session
      // bootstrap endpoint. It returns the active session user even when the
      // current auth provider is not Spotify.
      const res = await fetch("/api/spotify/me?optional=1", {
        credentials: "include",
      });
      if (!res.ok) {
        set({ user: null, authStatus: "unauth" });
        return;
      }

      const payload = await res.json().catch(() => null);
      const parsedUser = parseAuthUser(payload);
      const user = parsedUser
        ? await hydrateSpotifyOwnerId(parsedUser)
        : null;
      if (!user) {
        set({ user: null, authStatus: "unauth" });
        return;
      }

      set({ user, authStatus: "authed", error: null });
    } catch (err: unknown) {
      console.error("[auth.slice] checkSession failed:", err);
      set({
        user: null,
        authStatus: "unauth",
        error: errorMessage(err, "Failed to check authenticated session"),
      });
    }
  },

  logout: async () => {
    const currentUser = get?.().user;
    const provider = currentUser?.provider || "spotify";
    const sessionId = get?.().sessionId;
    const sessionActive = get?.().sessionActive === true;

    try {
      if (typeof sessionId === "string" && sessionId) {
        get?.().setBlockedSessionId?.(sessionId);
      }

      await requestMediaPlaybackStop("logout");

      if (provider === "spotify") {
        markSpotifyForceReauth("logout");
        await fetch("/api/spotify/player/pause", {
          method: "PUT",
          credentials: "include",
        });
      }
    } catch (err) {
      console.warn("[auth.slice] pause on logout failed:", err);
    }

    try {
      if (sessionActive && typeof sessionId === "string" && sessionId) {
        const abortRes = await fetch(`/api/sessions/${sessionId}/abort`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ reason: "logout" }),
        });
        if (!abortRes.ok) {
          const text = await abortRes.text().catch(() => "");
          console.warn("[auth.slice] abort on logout failed:", {
            status: abortRes.status,
            body: text.slice(0, 300),
          });
        }
      }

      const logoutPath =
        provider === "youtube"
          ? "/api/youtube/logout"
          : provider === "applemusic"
            ? "/api/apple/logout"
          : provider === "azure"
            ? "/api/azure/logout"
            : provider === "google"
              ? "/api/google/logout"
            : provider === "slack"
              ? "/api/slack/logout"
            : "/api/spotify/logout";

      await fetch(logoutPath, {
        method: "POST",
        credentials: "include",
      });
    } catch (err) {
      console.error("[auth.slice] logout failed:", err);
    } finally {
      get?.().resetSession?.();
      set({ user: null, authStatus: "unauth", error: null });
    }
  },
});
